import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "../../services/api/auth.service";
import {
  getAuthToken,
  getUserInfo,
  isAuthenticated,
  storeAuthToken,
  storeUserInfo,
  clearAuthData,
} from "../../utils/authUtils";
import { resetSessionExpiredFlag } from "../../services/utils/error.handler";

export const getUserType = createAsyncThunk(
  "auth/getUserType",
  async (username, { rejectWithValue }) => {
    try {
      const response = await authService.getUserType(username);
      return response.data.data[0];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get user type"
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.data.data[0];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);
export const forgotOtp = createAsyncThunk(
  "auth/forgotOtp",
  async (payload, { rejectWithValue }) => {
    try {
      // payload should be { username, email }
      const response = await authService.forgotOtp(payload);
      return response.data.data[0];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send an otp"
      );
    }
  }
);
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async (json, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOTP(json);
      return response.data.data[0];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset the password"
      );
    }
  }
);
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(payload);
      return response.data.data ? response.data.data[0] : response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset the password"
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (json, { rejectWithValue }) => {
    try {
      const response = await authService.changePassword(json);
      return response.data.data[0];
    } catch (error) {
      // Check if it's a password policy validation error (422 status)
      if (error.response?.status === 422 && 
          error.response?.data?.error?.details?.errors) {
        const errors = error.response.data.error.details.errors;
        const hasPasswordError = errors.some(err => err.field === 'new_password');
        
        if (hasPasswordError) {
          return rejectWithValue("New Password does not meet password policy requirements.");
        }
      }
      
      return rejectWithValue(
        error.response?.data?.message || "Failed to change the password"
      );
    }
  }
);
export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (json, { rejectWithValue }) => {
    try {
      const response = await authService.resendOtp(json);
  return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to resend the OTP"
      );
    }
  }
);

const initialState = {
  user: getUserInfo(),
  userType: null,
  token: getAuthToken(),
  isAuthenticated: isAuthenticated(),
  isLoading: false,
  error: null,
  loginStep: "username", // 'username' or 'password'
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action) => {
      const { user, tokens } = action.payload;
      state.user = user;
      state.token = tokens.access_token;
      state.isAuthenticated = true;

      // Store tokens securely
      storeAuthToken(
        tokens.access_token,
        tokens.refresh_token,
        tokens.expiresIn || 3600
      );
      storeUserInfo(user);
    },
    clearCredentials: (state) => {
      state.user = null;
      state.userType = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loginStep = "username";

      // Clear auth data securely
      clearAuthData();
    },
    setLoginStep: (state, action) => {
      state.loginStep = action.payload;
    },
    setUserType: (state, action) => {
      state.userType = action.payload;
    },
    updateCurrentRole: (state, action) => {
      // Update the user's current role when switching
      if (state.user) {
        state.user.primary_role_id = action.payload.roleId;
        state.user.current_role_name = action.payload.roleName;
        // Update in storage as well
        storeUserInfo(state.user);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserType.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userType = action.payload.userType;
        state.loginStep = "password";
      })
      .addCase(getUserType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.tokens.access_token;
        state.isAuthenticated = true;

        // Store tokens securely
        storeAuthToken(
          action.payload.tokens.access_token,
          action.payload.tokens.refresh_token,
          action.payload.tokens.expiresIn || 3600
        );
        storeUserInfo(action.payload.user);
        
        // Reset session expired flag on successful login
        resetSessionExpiredFlag();
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.userType = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loginStep = "username";

        // Clear auth data securely
        clearAuthData();
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout API fails, clear local state
        state.user = null;
        state.userType = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loginStep = "username";

        // Clear auth data securely
        clearAuthData();
      });
  },
});

export const {
  clearError,
  setCredentials,
  clearCredentials,
  setLoginStep,
  setUserType,
  updateCurrentRole,
} = authSlice.actions;
export default authSlice.reducer;
